import { useState } from 'react';
import type { DropEvent, FileRejection } from 'react-dropzone';
import { useEntityStore } from '@ContractBuilder/store';
import { getAttachmentDocumentPresignedUrl } from '@queries/getAttachmentDocumentPresignedUrl';
import {
  hasAttachmentValidFileExt,
  hasAttachmentValidPDFMarker,
  hasAttachmentValidSize,
} from '@root/helpers/attachments/validation-files';
import { hasAttachmentValidPdfStructure } from '@root/helpers/attachments/validation-structure';
import { isPdfEncrypted } from '@root/helpers/pdf/is-pdf-encrypted';
import { isEndorsementPath, isSubmissionPath, isTemplatePath } from '@root/src/utils/app-paths';
import { uploadAttachmentDocument } from '@src/mutations';

import { useAttachmentMutations } from '../hooks/useAttachmentMutations';
import { AttachmentUpload } from '../views/AttachmentUpload';

interface UploadState {
  error?: string;
  isFileDropped: boolean;
  isUploading: boolean;
}

export const AttachmentUploadController = ({ handleClose }: { handleClose: () => void }) => {
  const [attachmentName, setAttachmentName] = useState<string | undefined>(undefined);
  const [attachment, setAttachment] = useState<File | undefined>(undefined);

  const { submission } = useEntityStore(({ submission }) => ({ submission }));
  const { updateAttachmentStatus } = useAttachmentMutations();

  const templateId = isTemplatePath() ? submission?.id : undefined;
  const submissionId = isEndorsementPath() ? submission?.parentId : isSubmissionPath() ? submission?.id : undefined;
  const endorsementId = isEndorsementPath() ? submission?.id : undefined;

  const [uploadState, setUploadState] = useState<UploadState>({
    error: undefined,
    isFileDropped: false,
    isUploading: false,
  });

  const handleFileRejected = (fileRejections: FileRejection[], _event: DropEvent) => {
    const latestFileError = fileRejections?.pop()?.errors?.pop();
    const code = latestFileError?.code;
    let message = latestFileError?.message;

    if (code === 'file-too-small') {
      message = 'File is smaller than 4 bytes';
    }
    if (code === 'file-too-large') {
      message = 'File is larger than 10MB';
    }
    if (code === 'file-invalid-type') {
      message = 'File type must be pdf';
    }
    if (code === 'too-many-files') {
      message = 'Too many files';
    }

    setUploadState((current) => ({ ...current, error: message }));
  };

  const handleFileUpload = async (files: File[]) => {
    if (files.length < 1) {
      return;
    }

    setAttachment(files[0]);

    setUploadState((current) => ({ ...current, isFileDropped: true }));
  };

  const handleClickUpload = async () => {
    if (attachment) {
      setUploadState((current) => ({ ...current, isUploading: true }));

      const isEncrypted = await isPdfEncrypted(attachment);
      if (isEncrypted) {
        return setUploadState((current) => ({
          ...current,
          error: 'This file is password protected. Please remove the password and re-upload.',
          isUploading: false,
        }));
      }

      if (
        !hasAttachmentValidSize(attachment) ||
        !hasAttachmentValidFileExt(attachment) ||
        !(await hasAttachmentValidPDFMarker(attachment)) ||
        !(await hasAttachmentValidPdfStructure(await attachment.arrayBuffer()))
      ) {
        return setUploadState((current) => ({
          ...current,
          error: `This file can't be processed. Please check the file and re-upload a valid version.`,
          isUploading: false,
        }));
      }

      const response = await getAttachmentDocumentPresignedUrl({
        fileName: attachment.name,
        name: attachmentName ?? '',
        size: attachment.size,
        type: 'post',
        templateId,
        submissionId,
        endorsementId,
      });

      if (response?.status !== 201) {
        return setUploadState((current) => ({ ...current, error: 'Failed to upload', isUploading: false }));
      }

      await uploadAttachmentDocument(response.data.data.url, { file: attachment }, response.data.data.fields);
      await updateAttachmentStatus.mutateAsync({ attachmentId: response.data.data.id, attachmentStatus: 'ready' });

      handleClose();
    }
  };

  return (
    <div className="relative h-full">
      <div className="relative flex flex-col justify-center rounded-lg text-center">
        <AttachmentUpload
          error={uploadState.error}
          isUploadDisabled={!attachment || !attachmentName || uploadState.isUploading}
          attachmentName={attachmentName}
          attachmentFileName={attachment?.name || 'document.pdf'}
          isFileDropped={uploadState.isFileDropped}
          isUploading={uploadState.isUploading}
          onFileUpload={handleFileUpload}
          onFileRejected={handleFileRejected}
          setAttachmentName={setAttachmentName}
          handleClickUpload={handleClickUpload}
          handleClose={handleClose}
        />
      </div>
    </div>
  );
};
